import { type Program, type ProgramEventType, ProgramInviteType } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { Modal, handleAxiosError, message } from 'ui';
import type { EventContentObjectDataObject } from 'models/service';
import { useCreateProgramEventMutation } from '../../queries';
import { DataStepEventForm, type DefaultEventData, type DefaultEventFormValues } from './data-step-event-form';
import { TypeStepEventForm } from './type-step-event-form';

type EventFormStep = 'data' | 'type';

type P = {
  eventTypes: ProgramEventType[];
  initialDefaultData?: Partial<DefaultEventFormValues>;
  isOpened: boolean;
  onCancel: () => void;
  onRequestReturn?: () => void;
  onSuccess: (data: EventContentObjectDataObject) => Promise<void> | void;
  program?: Program;
};

const CreateEventFormModal = ({
  eventTypes,
  initialDefaultData,
  isOpened,
  onCancel,
  onRequestReturn,
  onSuccess,
  program,
}: P) => {
  const [step, setStep] = useState<EventFormStep>('type');
  const [eventType, setEventType] = useState<ProgramEventType>('conference');
  const [inviteType, setInviteType] = useState(ProgramInviteType.empty);
  const { isPending: isCreatingEvent, mutate: createEvent } = useCreateProgramEventMutation({ mode: 'create' });

  const isInviteOpened = Boolean(inviteType);

  const onModalBack = useMemo(() => {
    if (isInviteOpened) {
      return () => {
        setInviteType(ProgramInviteType.empty);
      };
    }

    if (step === 'data') {
      return () => {
        setStep('type');
      };
    }

    return onRequestReturn;
  }, [onRequestReturn, isInviteOpened, step]);

  const onSelectEventType = useCallback(
    (newEventType: ProgramEventType) => {
      setEventType(newEventType);
      setStep('data');
    },
    [setEventType, setStep],
  );

  const getTypeStepTitleName = () => {
    switch (eventType) {
      case 'conference':
        return 'Create Video Conference';
      case 'webinar':
        return 'Create Webinar / Webcast';
    }
  };

  const getTitleName = () => {
    switch (step) {
      case 'data':
        return getTypeStepTitleName();
      case 'type':
        return 'Create Event';
    }
  };

  const onSubmit = (data: DefaultEventData) => {
    createEvent(
      { data },
      {
        onError(error) {
          handleAxiosError(error, 'Failed to create event.');
        },
        async onSuccess(eventData) {
          message.success('Event was created.');
          await onSuccess(eventData.eventContentDataObject);
        },
      },
    );
  };

  return (
    <Modal closable={!isInviteOpened} isOpened={isOpened} onCancel={onCancel} title={getTitleName()} width="medium">
      <Modal.HugContent>
        {step === 'type' ? (
          <TypeStepEventForm onBack={onModalBack} onChange={onSelectEventType} options={eventTypes} />
        ) : (
          <DataStepEventForm
            eventType={eventType}
            initialDefaultData={initialDefaultData}
            inviteType={inviteType}
            isLoading={isCreatingEvent}
            mode="create"
            onCancel={onModalBack}
            onInviteesTypeChanged={setInviteType}
            onSubmit={onSubmit}
            program={program}
          />
        )}
      </Modal.HugContent>
    </Modal>
  );
};

export default CreateEventFormModal;
